import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSnackbar } from '@/hooks/useSnackbar';
import { GenericApiResponse } from '@/services/api';
import { ErrorCode, getErrorMessage } from '@/utils/errorMessages';

export type ErrorsState = Record<string, string | undefined>;

const useApiErrorHandling = (
  mapCodeToFieldErrors?: (code: ErrorCode, url?: string) => ErrorsState,
) => {
  const [apiErrors, setApiErrors] = useState<ErrorsState>({});
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['apiError']);

  const handleApiError = useCallback(
    (code: ErrorCode, url?: string) => {
      if (!mapCodeToFieldErrors) return;
      setApiErrors((previousErrors) => ({ ...previousErrors, ...mapCodeToFieldErrors(code, url) }));
    },
    [setApiErrors, mapCodeToFieldErrors],
  );

  const clearApiError = useCallback((type?: string) => {
    setApiErrors((previousErrors) => {
      if (type) {
        return { ...previousErrors, [type]: undefined };
      }
      return {};
    });
  }, []);

  function showApiError<T>(payload: GenericApiResponse<T> | Error | undefined): void {
    const message = getErrorMessage(payload, t);
    showSnackbar(message);
  }

  return { apiErrors, handleApiError, clearApiError, showApiError };
};

export default useApiErrorHandling;
